import * as React from 'react';

import {ChannelContainer} from 'shells/inbox/messages/channel-container';
import type {SenderAddress} from 'shells/inbox/messages/message-input/types';
import type {Recipient, CantSendStatusEnum} from 'shells/inbox/types';

import type {
    InboxThreadFragment,
    InboxChannelEventFragment as ChannelEvent,
    ThreadType,
} from 'nutshell-graphql-types';

import {useCreateNote} from '../../core/graphql/hooks/use-create-note';
import {useGetSessionUser} from '../../core/use-get-session-user';

import {useSendMessage} from '../facebook/graphql/hooks';
import {
    useGetInboxMessage,
    useSendChatMessage,
    useSendSharedInboxEmailMessage,
} from '../graphql/hooks';
import {useDeleteTextMessage, useResendTextMessage, useSendTextMessage} from '../sms/graphql/hooks';

type Props = {
    recipient: Recipient;
    isLoadingChannelEvents: boolean;
    channelEvents: ChannelEvent[];
    fetchMoreRows: () => any;
    hasNextPage: boolean;
    thread: InboxThreadFragment;
    channelId: string;
    externalNumber: string;
    fetchMostRecentChannelEvents: () => Promise<any>;
    refetchScheduledChannelEvents: () => Promise<any>;
    cantSendStatus: CantSendStatusEnum | null | undefined;
    threadType: ThreadType | null | undefined;
    refetchThreads?: () => any;
};

export function ThreadContent(props: Props) {
    const [newMessageId, setNewMessageId] = React.useState<any>(null);
    const [sendIsComplete, setSendIsComplete] = React.useState(false);
    const [sendIsFailed, setSendIsFailed] = React.useState(false);
    const [sendErrorMessage, setSendErrorMessage] = React.useState<string | null>(null);

    const {sendTextMessage, error: sendTextMessageError} = useSendTextMessage();
    const {sendChatMessage, error: sendChatMessageError} = useSendChatMessage();
    const {sendSharedInboxEmailMessage, error: sendSharedInboxEmailError} =
        useSendSharedInboxEmailMessage();
    const {sendMessage, error: sendMessageError} = useSendMessage();
    const {inboxMessage} = useGetInboxMessage(newMessageId);
    const {user} = useGetSessionUser();
    const {createNote} = useCreateNote();

    const {resendTextMessage} = useResendTextMessage();
    const onResendMessage = (messageId: string) => {
        return resendTextMessage({
            messageId,
            channelId: props.channelId,
            threadId: props.thread.id,
        }).then((response) => {
            const message =
                response &&
                response.data &&
                response.data.textResend &&
                response.data.textResend.message;

            const resentMessageId = message && message.id;
            setNewMessageId(resentMessageId);
        });
    };
    const {deleteTextMessage} = useDeleteTextMessage();
    const onDeleteMessage = (messageId: string) => {
        return deleteTextMessage({
            messageId,
            channelId: props.channelId,
            threadId: props.thread.id,
        });
    };

    React.useEffect(() => {
        if (inboxMessage) {
            if (inboxMessage.status === 'SENT') {
                setSendIsComplete(true);
                // clear complete state after a .5 seconds
                setTimeout(() => setSendIsComplete(false), 500);
            } else if (inboxMessage.status === 'FAILED') {
                setSendIsFailed(true);
            }
        }
    }, [inboxMessage]);

    React.useEffect(() => {
        if (sendMessageError) {
            setSendErrorMessage(sendMessageError);
        } else if (sendTextMessageError) {
            setSendErrorMessage(sendTextMessageError);
        } else if (sendChatMessageError) {
            setSendErrorMessage(sendChatMessageError);
        } else if (sendSharedInboxEmailError) {
            setSendErrorMessage(sendSharedInboxEmailError);
        } else {
            setSendErrorMessage(null);
        }
    }, [sendMessageError, sendTextMessageError, sendChatMessageError, sendSharedInboxEmailError]);

    const handleSend = (input: {
        content: string;
        snoozeDuration?: number;
        closeThread?: boolean;
        imageUrls?: string[];
        fromAddress?: SenderAddress;
    }): Promise<any> => {
        switch (props.threadType) {
            case 'EMAIL':
                if (!input.fromAddress) {
                    return Promise.reject(new Error('No from address provided'));
                }

                return sendSharedInboxEmailMessage({
                    content: input.content,
                    threadId: props.thread.id,
                    fromAddress: {
                        address: input.fromAddress.address,
                        display: input.fromAddress.display.replace(/\s*<.*?>/, ''),
                    },
                }).then((response) => {
                    const message =
                        response &&
                        response.data &&
                        response.data.messageSendFromSharedInbox &&
                        response.data.messageSendFromSharedInbox.message;

                    const messageId = message && message.id;
                    setNewMessageId(messageId);
                });
            case 'CHAT':
                return sendChatMessage({
                    content: input.content,
                    threadId: props.thread.id,
                    closeThread: input.closeThread,
                    imageUrls: input.imageUrls ? input.imageUrls : [],
                }).then((response) => {
                    const message =
                        response &&
                        response.data &&
                        response.data.messageSendForChat &&
                        response.data.messageSendForChat.message;

                    const messageId = message && message.id;
                    setNewMessageId(messageId);
                });
            case 'MESSENGER':
            case 'INSTAGRAM':
            case 'WHATSAPP':
                return sendMessage({
                    content: input.content,
                    threadId: props.thread.id,
                    closeThread: input.closeThread,
                    imageUrls: input.imageUrls ? input.imageUrls : [],
                }).then((response) => {
                    const message =
                        response &&
                        response.data &&
                        response.data.messageSend &&
                        response.data.messageSend.message;

                    const messageId = message && message.id;
                    setNewMessageId(messageId);
                });
            case 'SMS':
                return sendTextMessage({
                    externalNumber: props.externalNumber,
                    threadId: props.thread.id,
                    content: input.content,
                }).then((response) => {
                    const message =
                        response &&
                        response.data &&
                        response.data.inboxTextMessageSend &&
                        response.data.inboxTextMessageSend.message;

                    const messageId = message && message.id;
                    setNewMessageId(messageId);
                });
            default: {
                const errorMessage = props.threadType
                    ? `Unsupported thread type: ${props.threadType}`
                    : 'No thread type provided';

                return Promise.reject(new Error(errorMessage));
            }
        }
    };

    const handleNoteCreate = (note: string) => {
        return createNote({
            note: {
                value: note,
                entityId: props.thread.id,
            },
        });
    };

    return (
        <div className='flex flex-dir-col full-width full-height'>
            <div className='flex-grow min-height-0'>
                <ChannelContainer
                    onSend={handleSend}
                    onNoteCreate={handleNoteCreate}
                    recipient={props.recipient}
                    isLoadingChannelEvents={props.isLoadingChannelEvents}
                    channelEvents={props.channelEvents}
                    fetchMoreRows={props.fetchMoreRows}
                    hasNextPage={props.hasNextPage}
                    isSendComplete={sendIsComplete}
                    isSendFailed={sendIsFailed}
                    onResendMessage={onResendMessage}
                    onDeleteMessage={onDeleteMessage}
                    cantSendStatus={props.cantSendStatus}
                    fetchMostRecentChannelEvents={props.fetchMostRecentChannelEvents}
                    refetchScheduledChannelEvents={props.refetchScheduledChannelEvents}
                    showProgress={props.threadType !== 'CHAT'}
                    showSnoozeOptions={props.threadType === 'SMS'}
                    // @ts-expect-error
                    channelId={props.channelId}
                    sessionId={props.thread.externalChannel.sessionId || ''}
                    threadId={props.thread.id}
                    threadType={props.threadType || undefined}
                    sendErrorMessage={sendErrorMessage}
                    signature={user?.emailSignature}
                    threadSummary={props.thread.threadSummary}
                    userSendableEmailAddresses={user?.sendableEmailAddresses}
                />
            </div>
        </div>
    );
}
